/**
 * UserPortalAccess.js
 * Admin tab component for managing athlete portal access & audit history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Table, Alert, Spinner, Modal, Row, Col, Form } from 'react-bootstrap';
import { athleteAccessService } from '../../services/api';
import SendAccessLinkModal from './SendAccessLinkModal';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';

const UserPortalAccess = ({ userId, user, onAccessChanged }) => {
  const [loading, setLoading] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);

  // Login email editing state
  const [isEditingLoginEmail, setIsEditingLoginEmail] = useState(false);
  const [newLoginEmail, setNewLoginEmail] = useState('');
  const [loginEmailLoading, setLoginEmailLoading] = useState(false);
  const [loginEmailError, setLoginEmailError] = useState('');

  // Email validation warning modal state
  const [emailWarningState, setEmailWarningState] = useState({
    warnings: [],
    onIgnore: null,
  });

  const loadAccessData = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError('');

      const [infoRes, userAuditRes, parentAuditRes] = await Promise.all([
        athleteAccessService.getAccessInfo(userId),
        athleteAccessService.getAuditHistory('User', userId),
        athleteAccessService.getAuditHistory('Parent', userId).catch(() => ({ success: true, data: [] }))
      ]);

      if (infoRes.success) {
        setAccessInfo(infoRes.data);
      }

      const combinedAudits = [
        ...(userAuditRes.success && Array.isArray(userAuditRes.data) ? userAuditRes.data : []),
        ...(parentAuditRes.success && Array.isArray(parentAuditRes.data) ? parentAuditRes.data : [])
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setAuditLogs(combinedAudits);
    } catch (err) {
      console.error('Error loading athlete access data:', err);
      setError('Errore durante il caricamento dei dati di accesso');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAccessData();
  }, [loadAccessData]);

  const handleRevokeAccess = async () => {
    try {
      setRevokeLoading(true);
      setError('');
      const result = await athleteAccessService.revokeAccess(userId);
      if (result.success) {
        setSuccess('Accesso al portale revocato con successo');
        setShowRevokeModal(false);
        await loadAccessData();
        if (onAccessChanged) onAccessChanged();
      } else {
        setError(result.message || 'Errore nella revoca dell\'accesso');
      }
    } catch (err) {
      console.error('Error revoking access:', err);
      setError('Errore di connessione al server');
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleSaveLoginEmail = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = newLoginEmail.trim();
    if (!trimmed) {
      setLoginEmailError('L\'email di login è obbligatoria');
      return;
    }

    if (!bypassEmailWarning) {
      const warnings = checkEmailsForWarnings([
        {
          label: 'Email di login',
          value: trimmed,
          selector: 'input[data-testid="new-user-login-email-input"]',
        },
      ]);
      if (warnings.length > 0) {
        setEmailWarningState({
          warnings,
          onIgnore: () => handleSaveLoginEmail(null, true),
        });
        return;
      }
    }

    try {
      setLoginEmailLoading(true);
      setLoginEmailError('');
      const res = await athleteAccessService.updateUserLoginEmail(userId, trimmed);
      if (res.success) {
        setSuccess(res.message || 'Email di login aggiornata con successo');
        setIsEditingLoginEmail(false);
        await loadAccessData();
        if (onAccessChanged) onAccessChanged();
      } else {
        setLoginEmailError(res.message || 'Errore durante l\'aggiornamento dell\'email di login');
      }
    } catch (err) {
      console.error('Error updating user login email:', err);
      setLoginEmailError('Errore di connessione al server');
    } finally {
      setLoginEmailLoading(false);
    }
  };

  const renderChangedFields = (changedFieldsJson) => {
    if (!changedFieldsJson) return <span className="text-muted">-</span>;

    try {
      const fields = typeof changedFieldsJson === 'string'
        ? JSON.parse(changedFieldsJson)
        : changedFieldsJson;

      if (!fields || Object.keys(fields).length === 0) {
        return <span className="text-muted">-</span>;
      }

      return (
        <ul className="list-unstyled mb-0 small">
          {Object.entries(fields).map(([fieldName, change]) => {
            const oldVal = change?.old !== null && change?.old !== undefined ? String(change.old) : '<em>vuoto</em>';
            const newVal = change?.new !== null && change?.new !== undefined ? String(change.new) : '<em>vuoto</em>';
            return (
              <li key={fieldName} className="mb-1">
                <strong>{fieldName}:</strong>{' '}
                <span className="text-danger text-decoration-line-through" dangerouslySetInnerHTML={{ __html: oldVal }} />
                {' → '}
                <span className="text-success fw-bold" dangerouslySetInnerHTML={{ __html: newVal }} />
              </li>
            );
          })}
        </ul>
      );
    } catch (e) {
      return <span className="text-muted">{String(changedFieldsJson)}</span>;
    }
  };

  if (loading && !accessInfo) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Caricamento informazioni di accesso...</p>
      </div>
    );
  }

  return (
    <div className="user-portal-access">
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {/* Access Status Card */}
      <Card className="mb-4 shadow-sm border-0 bg-light">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h5 className="mb-3">
                <i className="fas fa-user-shield me-2 text-primary"></i>
                Stato Accesso Portale
              </h5>
              <div className="d-flex align-items-center gap-3 mb-2">
                <span className="fw-bold">Stato:</span>
                {accessInfo?.hasAccess ? (
                  <Badge bg="success" className="fs-6 px-3 py-2">
                    <i className="fas fa-check-circle me-1"></i>
                    Attivo
                  </Badge>
                ) : (
                  <Badge bg="secondary" className="fs-6 px-3 py-2">
                    <i className="fas fa-times-circle me-1"></i>
                    Non configurato
                  </Badge>
                )}
              </div>

              <div className="mb-2">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <strong>Email di login:</strong>
                  {!isEditingLoginEmail && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => {
                        setIsEditingLoginEmail(true);
                        setNewLoginEmail(accessInfo?.loginEmail || '');
                        setLoginEmailError('');
                      }}
                      data-testid="edit-user-login-email-btn"
                    >
                      <i className="fas fa-pencil-alt me-1"></i>
                      {accessInfo?.loginEmail ? 'Modifica' : 'Imposta'}
                    </Button>
                  )}
                </div>

                {!isEditingLoginEmail ? (
                  <span className="font-monospace text-primary" data-testid="current-user-login-email">
                    {accessInfo?.loginEmail || <span className="text-muted fst-italic">Non impostata</span>}
                  </span>
                ) : (
                  <Form onSubmit={handleSaveLoginEmail} className="mt-1">
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="email"
                        size="sm"
                        value={newLoginEmail}
                        onChange={(e) => {
                          setNewLoginEmail(e.target.value);
                          if (loginEmailError) setLoginEmailError('');
                        }}
                        isInvalid={!!loginEmailError}
                        placeholder="Inserisci la nuova email di login"
                        data-testid="new-user-login-email-input"
                        disabled={loginEmailLoading}
                        required
                      />
                      <Form.Control.Feedback type="invalid" className="small">
                        {loginEmailError}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={loginEmailLoading}
                        data-testid="save-user-login-email-btn"
                      >
                        {loginEmailLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" /> Salvataggio...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1"></i> Salva
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        disabled={loginEmailLoading}
                        onClick={() => {
                          setIsEditingLoginEmail(false);
                          setLoginEmailError('');
                        }}
                      >
                        Annulla
                      </Button>
                    </div>
                  </Form>
                )}
              </div>

              {accessInfo?.lastLogin && (
                <div className="text-muted small">
                  <i className="fas fa-clock me-1"></i>
                  Ultimo accesso:{' '}
                  {new Date(accessInfo.lastLogin).toLocaleString('it-IT', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button
                variant="primary"
                className="me-2 mb-2 w-100 w-md-auto"
                onClick={() => setShowSendModal(true)}
              >
                <i className="fas fa-paper-plane me-2"></i>
                Invia Link Accesso
              </Button>
              {accessInfo?.hasAccess && (
                <Button
                  variant="outline-danger"
                  className="mb-2 w-100 w-md-auto"
                  onClick={() => setShowRevokeModal(true)}
                >
                  <i className="fas fa-ban me-2"></i>
                  Revoca Accesso
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Token History */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-white py-3">
          <h6 className="mb-0 fw-bold">
            <i className="fas fa-ticket-alt me-2 text-primary"></i>
            Storico Token di Accesso
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          {accessInfo?.tokens && accessInfo.tokens.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Token (estratto)</th>
                    <th>Stato</th>
                    <th>Creato il</th>
                    <th>Scadenza</th>
                  </tr>
                </thead>
                <tbody>
                  {accessInfo.tokens.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <span className="font-monospace text-muted">
                          {t.token.substring(0, 12)}...
                        </span>
                      </td>
                      <td>
                        {t.isUsed ? (
                          <Badge bg="secondary">Utilizzato</Badge>
                        ) : t.isExpired ? (
                          <Badge bg="danger">Scaduto</Badge>
                        ) : (
                          <Badge bg="success">Attivo</Badge>
                        )}
                      </td>
                      <td>{new Date(t.createdAt).toLocaleString('it-IT')}</td>
                      <td>{new Date(t.expiresAt).toLocaleString('it-IT')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              Nessun token di accesso generato finora.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Audit Log / Change History */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3">
          <h6 className="mb-0 fw-bold">
            <i className="fas fa-history me-2 text-primary"></i>
            Storico Modifiche Anagrafica (Audit Log)
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          {auditLogs && auditLogs.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Data</th>
                    <th>Entità</th>
                    <th>Azione</th>
                    <th>Modificato da</th>
                    <th>Campi Modificati</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="small text-nowrap">
                        {new Date(log.createdAt).toLocaleString('it-IT')}
                      </td>
                      <td>
                        <Badge bg={log.entityType === 'Parent' ? 'info' : 'primary'}>
                          {log.entityType === 'Parent' ? 'Genitore' : 'Atleta'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={log.action === 'INSERT' || log.action === 'ADDED' ? 'success' : log.action === 'UPDATE' || log.action === 'MODIFIED' ? 'warning' : 'secondary'}>
                          {log.action}
                        </Badge>
                      </td>
                      <td>
                        {log.changedByUser ? (
                          <div className="small">
                            <strong>{log.changedByUser.firstName} {log.changedByUser.lastName}</strong>
                            <div className="text-muted">{log.changedByUser.email}</div>
                          </div>
                        ) : (
                          <span className="text-muted small">Sistema / Atleta</span>
                        )}
                      </td>
                      <td>{renderChangedFields(log.changedFields)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              Nessuna modifica registrata nell'audit log.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Send Link Modal */}
      <SendAccessLinkModal
        show={showSendModal}
        onHide={() => setShowSendModal(false)}
        user={user}
        onSuccess={(msg) => {
          setSuccess(msg);
          setShowSendModal(false);
          loadAccessData();
          if (onAccessChanged) onAccessChanged();
        }}
        onError={(msg) => setError(msg)}
      />

      {/* Revoke Access Confirmation Modal */}
      <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Conferma Revoca Accesso
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler revocare l'accesso al portale per <strong>{user?.firstName} {user?.lastName}</strong>?
          </p>
          <p className="text-muted small mb-0">
            L'email di login e la password verranno cancellate. L'atleta non potrà più accedere al portale fino a nuovo invito.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRevokeModal(false)} disabled={revokeLoading}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleRevokeAccess} disabled={revokeLoading}>
            {revokeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Revoca in corso...
              </>
            ) : (
              'Conferma Revoca'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Warning Modal */}
      <EmailWarningModal
        show={emailWarningState.warnings.length > 0}
        warnings={emailWarningState.warnings}
        onCorrect={() => setEmailWarningState({ warnings: [], onIgnore: null })}
        onIgnoreAndProceed={() => {
          const proceed = emailWarningState.onIgnore;
          setEmailWarningState({ warnings: [], onIgnore: null });
          if (proceed) proceed();
        }}
      />
    </div>
  );
};

export default UserPortalAccess;
